import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../contexts/AuthContext";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Plus, CheckCircle, Clock, Trash2 } from "lucide-react";
import { toast } from "sonner";

const MONTHS_ES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const formatCurrency = (v) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(v);
const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;
const years = Array.from({ length: 6 }, (_, i) => String(currentYear - 3 + i));

export default function CommunityFeesPage() {
  const [fees, setFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [filterYear, setFilterYear] = useState(String(currentYear));
  const [filterMonth, setFilterMonth] = useState("all");
  const [genMonth, setGenMonth] = useState(String(currentMonth));
  const [genYear, setGenYear] = useState(String(currentYear));

  const load = useCallback(async () => {
    try {
      let params = {};
      if (filterYear !== "all") params.year = parseInt(filterYear);
      if (filterMonth !== "all") params.month = parseInt(filterMonth);
      const { data } = await axios.get(`${API}/community/fees`, { params });
      setFees(data);
    } catch (e) { if (e.response?.status !== 401) toast.error("Error cargando"); }
    finally { setLoading(false); }
  }, [filterYear, filterMonth]);
  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/community/fees/generate`, { month: parseInt(genMonth), year: parseInt(genYear) });
      if (data.created === 0) toast.info("Las cuotas de este mes ya estaban generadas");
      else toast.success(`${data.created} cuota(s) generada(s)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setGenerating(false); }
  };

  const toggleStatus = async (fee) => {
    const newStatus = fee.status === "pagada" ? "pendiente" : "pagada";
    try { await axios.put(`${API}/community/fees/${fee.id}/status`, { status: newStatus }); toast.success(`Cuota ${newStatus}`); load(); }
    catch { toast.error("Error"); }
  };

  const handleDelete = async (fee) => {
    if (!window.confirm("¿Eliminar esta cuota?")) return;
    try { await axios.delete(`${API}/community/fees/${fee.id}`); toast.success("Eliminada"); load(); }
    catch { toast.error("Error"); }
  };

  const totalPending = fees.filter(f => f.status === "pendiente").reduce((s, f) => s + f.amount, 0);
  const totalPaid = fees.filter(f => f.status === "pagada").reduce((s, f) => s + f.amount, 0);

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Cargando...</div>;

  return (
    <div data-testid="community-fees-page">
      <div className="mb-8">
        <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-gray-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Cuotas Comunidad</h2>
        <p className="text-sm text-gray-500 mt-1">{fees.length} cuotas | Cobrado: {formatCurrency(totalPaid)} | Pendiente: {formatCurrency(totalPending)}</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-sm p-6 mb-6">
        <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-gray-900 mb-4">Generar cuotas mensuales</h3>
        <div className="flex items-end gap-4">
          <div><Label className="text-xs font-semibold text-gray-500 mb-1 block uppercase tracking-wider">Mes</Label>
            <Select value={genMonth} onValueChange={setGenMonth}><SelectTrigger className="w-40 rounded-sm"><SelectValue /></SelectTrigger><SelectContent>{MONTHS_ES.map((m, i) => <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs font-semibold text-gray-500 mb-1 block uppercase tracking-wider">Año</Label>
            <Select value={genYear} onValueChange={setGenYear}><SelectTrigger className="w-28 rounded-sm"><SelectValue /></SelectTrigger><SelectContent>{years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent></Select></div>
          <button onClick={handleGenerate} disabled={generating} className="bg-[#002FA7] text-white hover:bg-[#00227B] rounded-sm px-5 py-2 text-sm font-semibold transition-all flex items-center gap-2 h-9 disabled:opacity-50" data-testid="generate-fees-button">
            <Plus className="w-4 h-4" /> {generating ? "Generando..." : "Generar"}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-4 mb-4">
        <Select value={filterYear} onValueChange={v => { setFilterYear(v); setLoading(true); }}><SelectTrigger className="w-28 rounded-sm"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Todos</SelectItem>{years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent></Select>
        <Select value={filterMonth} onValueChange={v => { setFilterMonth(v); setLoading(true); }}><SelectTrigger className="w-36 rounded-sm"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Todos</SelectItem>{MONTHS_ES.map((m, i) => <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>)}</SelectContent></Select>
      </div>

      <div className="bg-white border border-gray-200 rounded-sm">
        <Table>
          <TableHeader><TableRow className="bg-gray-50">
            <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Propietario</TableHead>
            <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Piso</TableHead>
            <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Periodo</TableHead>
            <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold text-right">Importe</TableHead>
            <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Estado</TableHead>
            <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold text-right">Acciones</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {fees.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-400">No hay cuotas</TableCell></TableRow>
            ) : fees.map(f => (
              <TableRow key={f.id} className="hover:bg-gray-50">
                <TableCell className="text-sm font-medium text-gray-900 py-3 px-4">{f.owner_name}</TableCell>
                <TableCell className="text-sm text-gray-600 py-3 px-4">{f.floor_door}</TableCell>
                <TableCell className="text-sm text-gray-600 py-3 px-4">{MONTHS_ES[f.month-1]} {f.year}</TableCell>
                <TableCell className="text-sm font-medium text-gray-900 py-3 px-4 text-right">{formatCurrency(f.amount)}</TableCell>
                <TableCell className="py-3 px-4">
                  <button onClick={() => toggleStatus(f)} className="cursor-pointer">
                    <span className={`status-badge ${f.status === "pagada" ? "status-pagada" : "status-pendiente"}`}>
                      {f.status === "pagada" ? <CheckCircle className="w-3 h-3 mr-1" /> : <Clock className="w-3 h-3 mr-1" />}{f.status}
                    </span>
                  </button>
                </TableCell>
                <TableCell className="py-3 px-4 text-right">
                  <button onClick={() => handleDelete(f)} className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
